import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {Button, Table, Input} from 'reactstrap';
import {putStudentExtraTime, putStudentReopen} from '../store/actions/fetchActions';

// Isolated editor for per-student testing-accommodation extra time (in minutes).
// Kept separate from the DataTable-managed roster so controlled inputs don't fight jQuery.
const ExtraTimeEditor = ({students}) => {
    const dispatch = useDispatch();

    const seed = () =>
        Object.fromEntries(
            (students || []).map(s => [
                s.student_id,
                {
                    mc: s.extra_mc_minutes || 0,
                    fr: s.extra_fr_minutes || 0,
                    mc2: s.extra_mc2_minutes || 0,
                    fr2: s.extra_fr2_minutes || 0,
                },
            ])
        );

    const [edits, setEdits] = useState(seed);
    const [bulkMc, setBulkMc] = useState('');
    const [bulkFr, setBulkFr] = useState('');
    const [saving, setSaving] = useState(false);
    const [savedMsg, setSavedMsg] = useState('');
    // Part 2 applies to multi-section exams (2 MC and/or 2 FRQ parts, e.g. Calculus/Precalculus)
    const [part, setPart] = useState(1);

    const mcField = part === 2 ? 'mc2' : 'mc';
    const frField = part === 2 ? 'fr2' : 'fr';
    const partLabel = part === 2 ? 'Part 2' : 'Part 1';

    const toInt = v => Math.max(0, parseInt(v, 10) || 0);

    const setVal = (id, field, value) =>
        setEdits(prev => ({...prev, [id]: {...prev[id], [field]: toInt(value)}}));

    const applyBulk = () => {
        const mc = toInt(bulkMc);
        const fr = toInt(bulkFr);
        setEdits(prev =>
            Object.fromEntries(
                Object.entries(prev).map(([id, v]) => [id, {...v, [mcField]: mc, [frField]: fr}])
            )
        );
    };

    const reopen = (student, section) => {
        const label = section === 'mc' ? 'Multiple Choice' : 'Free Response';
        const ok = window.confirm(
            `Reopen ${label} ${partLabel} for ${student.name}? They will get fresh full time and ` +
                `can re-enter that section. Their submitted answers are kept until they resubmit.`
        );
        if (!ok) return;
        const payload = {student_id: student.student_id};
        if (section === 'mc') {
            if (part === 2) payload.reopen_mc2 = true;
            else payload.reopen_mc = true;
        } else if (part === 2) {
            payload.reopen_fr2 = true;
        } else {
            payload.reopen_fr = true;
        }
        dispatch(putStudentReopen(payload));
    };

    const save = () => {
        setSaving(true);
        setSavedMsg('');
        const updates = Object.entries(edits).map(([student_id, v]) => ({
            student_id: Number(student_id),
            extra_mc_minutes: v.mc,
            extra_fr_minutes: v.fr,
            extra_mc2_minutes: v.mc2,
            extra_fr2_minutes: v.fr2,
        }));
        Promise.resolve(dispatch(putStudentExtraTime(updates)))
            .then(() => setSavedMsg('Saved.'))
            .catch(() => setSavedMsg('Could not save — please try again.'))
            .finally(() => setSaving(false));
    };

    return (
        <div className='mb-3 p-3 border rounded'>
            <div className='mb-3'>
                <Button
                    color='primary'
                    outline={part !== 1}
                    size='sm'
                    className='mr-2'
                    onClick={() => setPart(1)}
                >
                    Part 1 of MC and FRQ
                </Button>{' '}
                <Button
                    color='primary'
                    outline={part !== 2}
                    size='sm'
                    onClick={() => setPart(2)}
                >
                    Show Extended Time for Part 2 of MC and FRQ
                </Button>
                <div className='small text-muted mt-1'>
                    Part 2 only applies to multi-section exams (two MC and/or two FRQ parts, e.g.
                    Calculus and Precalculus). Editing <strong>{partLabel}</strong>.
                </div>
            </div>
            <div className='d-flex align-items-end flex-wrap' style={{gap: '10px'}}>
                <div>
                    <label className='mb-0 small'>
                        Apply to all below — MC {partLabel} (min)
                    </label>
                    <Input
                        type='number'
                        min='0'
                        value={bulkMc}
                        onChange={e => setBulkMc(e.target.value)}
                        style={{width: '120px'}}
                    />
                </div>
                <div>
                    <label className='mb-0 small'>FR {partLabel} (min)</label>
                    <Input
                        type='number'
                        min='0'
                        value={bulkFr}
                        onChange={e => setBulkFr(e.target.value)}
                        style={{width: '120px'}}
                    />
                </div>
                <Button color='secondary' onClick={applyBulk}>
                    Apply to all below
                </Button>
                <Button color='success' onClick={save} disabled={saving}>
                    {saving ? 'Saving…' : 'Save extended time'}
                </Button>
                {savedMsg && <span className='align-self-center text-muted'>{savedMsg}</span>}
            </div>
            <Table size='sm' className='mt-3'>
                <thead>
                    <tr>
                        <th>Student</th>
                        <th>Name</th>
                        <th>Login Code</th>
                        <th>Extra MC — {partLabel} (min)</th>
                        <th>Extra FR — {partLabel} (min)</th>
                        <th>Reopen locked section ({partLabel})</th>
                    </tr>
                </thead>
                <tbody>
                    {(students || []).map(s => (
                        <tr key={s.student_id}>
                            <td>{s.student}</td>
                            <td>{s.name}</td>
                            <td className='text-center'>{s.login_code}</td>
                            <td>
                                <Input
                                    type='number'
                                    min='0'
                                    value={edits[s.student_id]?.[mcField] ?? 0}
                                    onChange={e => setVal(s.student_id, mcField, e.target.value)}
                                    style={{width: '90px'}}
                                />
                            </td>
                            <td>
                                <Input
                                    type='number'
                                    min='0'
                                    value={edits[s.student_id]?.[frField] ?? 0}
                                    onChange={e => setVal(s.student_id, frField, e.target.value)}
                                    style={{width: '90px'}}
                                />
                            </td>
                            <td>
                                <Button
                                    color='warning'
                                    size='sm'
                                    className='mr-1'
                                    onClick={() => reopen(s, 'mc')}
                                >
                                    Reopen MC
                                </Button>{' '}
                                <Button color='warning' size='sm' onClick={() => reopen(s, 'fr')}>
                                    Reopen FR
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

export default ExtraTimeEditor;
